import { firstTwoNameParts, prettyDate } from './pdfParser'
import type { ParsedTicket, ReservationValues } from './types'

type EditableKey = keyof ReservationValues
type FieldDefinition = { id: string; key: EditableKey; label: string }
type FieldChangeHandler = (key: EditableKey, value: string) => void

const editableFields: FieldDefinition[] = [
  { id: 'booking-id', key: 'bookingId', label: 'Booking ID' },
  { id: 'booking-ref', key: 'bookingReference', label: 'Booking Reference No.' },
  { id: 'client', key: 'client', label: 'Client' },
  { id: 'member-id', key: 'memberId', label: 'Member ID' },
  { id: 'rooms', key: 'rooms', label: 'Number of Rooms' },
  { id: 'adults', key: 'adults', label: 'Number of Adults' },
  { id: 'room-type', key: 'roomType', label: 'Room Type' },
  { id: 'guest-list', key: 'guestList', label: 'Guest List' },
]

export function formatClientName(value: string): string {
  const normalized = firstTwoNameParts(value).toLocaleLowerCase()
  const firstLetter = normalized.search(/\p{L}/u)
  if (firstLetter < 0) return normalized

  const firstCharacter = normalized[firstLetter]
  if (!firstCharacter) return normalized

  return (
    normalized.slice(0, firstLetter) +
    firstCharacter.toLocaleUpperCase() +
    normalized.slice(firstLetter + 1)
  )
}

export function formatGuestName(value: string): string {
  return firstTwoNameParts(value)
    .toLocaleLowerCase()
    .replace(/\b\p{L}/gu, (letter) => letter.toLocaleUpperCase())
}

function fieldText(element: HTMLElement): string {
  return element.innerText.replace(/\u00a0/g, ' ').trim()
}

export function initializeHotelTemplate(
  doc: Document,
  ticket: ParsedTicket,
  values: ReservationValues,
  onChange: FieldChangeHandler,
): boolean {
  const page = doc.getElementById('page')

  if (!page) return false

  page.classList.add('editing')

  for (const field of editableFields) {
    const element = doc.getElementById(field.id)

    if (!element) return false

    const isNameField =
      field.key === 'client' || field.key === 'guestList'

    const fieldValue = isNameField
      ? firstTwoNameParts(values[field.key])
      : values[field.key]

    element.textContent =
      field.key === 'client'
        ? formatClientName(fieldValue)
        : field.key === 'guestList'
          ? formatGuestName(fieldValue)
          : fieldValue

    element.style.fontFamily = 'Arial, Helvetica, sans-serif'
    element.style.fontSize = '14px'
    element.style.fontWeight = [
      'bookingId',
      'bookingReference',
      'client',
      'memberId',
      'rooms',
      'adults',
      'roomType',
    ].includes(field.key)
      ? '700'
      : '400'

    element.contentEditable = 'true'
    element.setAttribute('aria-label', field.label)
    element.setAttribute('role', 'textbox')
    element.spellcheck = false

    element.onkeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
      }
    }

    element.oninput = () => {
      onChange(field.key, fieldText(element))
    }

    if (isNameField) {
      element.onblur = () => {
        const twoPartName = firstTwoNameParts(fieldText(element))

        const formatted =
          field.key === 'client'
            ? formatClientName(twoPartName)
            : field.key === 'guestList'
              ? formatGuestName(twoPartName)
              : twoPartName

        element.textContent = formatted
        onChange(field.key, formatted)
      }
    }
  }

  const arrival = doc.getElementById('arrival-date')
  const departure = doc.getElementById('departure-date')

  if (!arrival || !departure) return false

  arrival.textContent = prettyDate(ticket.arrivalDate)
  departure.textContent = prettyDate(ticket.departureDate)

  arrival.contentEditable = 'false'
  departure.contentEditable = 'false'

  return true
}

export function readHotelTemplateValues(
  doc: Document,
  fallback: ReservationValues,
): ReservationValues {
  const current = { ...fallback }

  for (const field of editableFields) {
    const element = doc.getElementById(field.id)

    if (element) {
      const value = fieldText(element)

      const isNameField =
        field.key === 'client' || field.key === 'guestList'

      const fieldValue = isNameField
        ? firstTwoNameParts(value)
        : value

      current[field.key] =
        field.key === 'client'
          ? formatClientName(fieldValue)
          : field.key === 'guestList'
            ? formatGuestName(fieldValue)
            : fieldValue
    }
  }

  return current
}
